import './Exam.css';
import { useParams, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react'; 
import api from '../../api/axios.js';
import { Box, Text, Flex, Table, Radio, Button } from '@radix-ui/themes';
import * as RadioGroup from '@radix-ui/react-radio-group';

const Exam = () => {
    const { id } = useParams();
    const [lesson, setLesson] = useState(null);
    const [exam, setExam] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showError, setShowError] = useState(false);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const navigate = useNavigate();
    useEffect(() => {
        const fetchExam = async () => {
            try {
                const lessonResponse = await api.get(`/lessons/${id}`);
                setLesson(lessonResponse.data);

                const response = await api.get(`/exams/lesson/${id}`);
                setExam(response.data);

                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        }
        fetchExam();
    }, [id]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!exam) return <div>Exam not found</div>;

    const handleAnswerChange = (questionId, value) => {
        setAnswers(prev => ({
          ...prev,
          [questionId]: value
        }));
        setShowError(false);

        // Remove the red highlight from the question
        const questionElement = document.getElementById(`question-${questionId}`);
        if (questionElement) {
            const textElement = questionElement.querySelector('Text');
            if (textElement) {
                textElement.style.color = 'inherit';
                textElement.style.fontWeight = 'normal';
            }
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Check if all questions are answered
        const unansweredQuestions = exam.filter(q => !answers[q.id]);
        
        if (unansweredQuestions.length > 0) {
            setShowError(true);
            // Scroll to first unanswered question
            const firstUnanswered = document.getElementById(`question-${unansweredQuestions[0].id}`);
            if (firstUnanswered) {
                firstUnanswered.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }

        // Format answers into an array of objects with exam_id and selected_answer
        const formattedAnswers = Object.entries(answers).map(([examId, value]) => ({
            exam_id: parseInt(examId),
            select: value
        }));

        // If all questions are answered, proceed with submission
        console.log('Submitted answers:', answers);

        // Send answers to the server
        try {
            const response = await api.post(`/exams/submit`, {
                lessonId: id,
                userId: user.id,
                answers: formattedAnswers
            });
            console.log('Submission successful:', response.data);

            // Redirect to the results page
            navigate(`/lessons`);

        } catch (error) {
            if (error.response?.status === 400) {
                alert(error.response.data.message);
            } else {
                console.error('Error submitting answers:', error);
                alert('Failed to submit exam. Please try again.');
            }
        }
    };

    return (
        <div id="exam" style={{ padding: '20px 100px' }}>
            {/* Exams Table */}
            <Box mt="6">
                <Text size="6" mb="4">Exam Questions</Text><br />
                <div id="lesson-info" style={{ textAlign: 'left' }}>
                    <Text size="3" mb="4">Lesson: {lesson.name}</Text><br />
                    <Text size="3" mb="4">Description: {lesson.description}</Text><br />
                </div>
                {showError && (
                    <Box mb="4" p="3" style={{ 
                        backgroundColor: '#ffebee', 
                        color: '#c62828',
                        borderRadius: '4px',
                        marginBottom: '20px',
                        transition: 'all 0.3s ease',
                        opacity: showError ? 1 : 0
                    }}>
                        <Text weight="bold">Please answer all questions before submitting.</Text>
                    </Box>
                )}
                {exam.length > 0 ? (
                    <form onSubmit={handleSubmit}>
                        <Table.Root>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeaderCell>No.</Table.ColumnHeaderCell>
                                    <Table.ColumnHeaderCell>Question & Choice</Table.ColumnHeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>    
                                {exam.map((question, index) => (
                                    <React.Fragment key={question.id}>
                                    <Table.Row id={`question-${question.id}`}>
                                        <Table.Cell>{index + 1}</Table.Cell>
                                        <Table.Cell>
                                            <Text weight={!answers[question.id] && showError ? "bold" : "normal"} 
                                                style={{ color: !answers[question.id] && showError ? '#c62828' : 'inherit', transition: 'color 0.3s ease' }}>
                                                {question.question}
                                            </Text>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell></Table.Cell>
                                        <Table.Cell>
                                            <RadioGroup.Root
                                                className="radio-group-root"
                                                required
                                                value={answers[question.id]?.toString() || ''}
                                                onValueChange={(value) => handleAnswerChange(question.id, parseInt(value))}
                                                style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                                            >
                                                <div className="radio-group-label">
                                                    <RadioGroup.Item className="radio-group-item" value="1" id={`q${question.id}-1`}>
                                                        <RadioGroup.Indicator className="radio-group-indicator" />
                                                        <label htmlFor={`q${question.id}-1`}>{question.choice1}</label>
                                                    </RadioGroup.Item>
                                                </div>
                                                <div className="radio-group-label">
                                                    <RadioGroup.Item className="radio-group-item" value="2" id={`q${question.id}-2`}>
                                                        <RadioGroup.Indicator className="radio-group-indicator" />
                                                        <label htmlFor={`q${question.id}-2`}>{question.choice2}</label>
                                                    </RadioGroup.Item>
                                                </div>
                                                {question.choice3 && (<div className="radio-group-label">
                                                    <RadioGroup.Item className="radio-group-item" value="3" id={`q${question.id}-3`}>
                                                        <RadioGroup.Indicator className="radio-group-indicator" />
                                                        <label htmlFor={`q${question.id}-3`}>{question.choice3}</label>
                                                    </RadioGroup.Item>
                                                </div>
                                                )}
                                                {question.choice4 && (
                                                <div className="radio-group-label">
                                                    <RadioGroup.Item className="radio-group-item" value="4" id={`q${question.id}-4`}>
                                                        <RadioGroup.Indicator className="radio-group-indicator" />
                                                        <label htmlFor={`q${question.id}-4`}>{question.choice4}</label>
                                                    </RadioGroup.Item>
                                                </div>
                                                )}
                                                {question.choice5 && (
                                                <div className="radio-group-label">
                                                    <RadioGroup.Item className="radio-group-item" value="5" id={`q${question.id}-5`}>
                                                        <RadioGroup.Indicator className="radio-group-indicator" />
                                                        <label htmlFor={`q${question.id}-5`}>{question.choice5}</label>
                                                    </RadioGroup.Item>
                                                </div>
                                                )}
                                                {question.choice6 && (
                                                <div className="radio-group-label">
                                                    <RadioGroup.Item className="radio-group-item" value="6" id={`q${question.id}-6`}>
                                                        <RadioGroup.Indicator className="radio-group-indicator" />
                                                        <label htmlFor={`q${question.id}-6`}>{question.choice6}</label>
                                                    </RadioGroup.Item>
                                                </div>
                                                )}
                                            </RadioGroup.Root>
                                        </Table.Cell>
                                    </Table.Row>
                                </React.Fragment>
                                ))}
                            </Table.Body>
                        </Table.Root>
                        <Flex justify="end" mt="4">
                            <Button type="submit" variant="solid" color="blue" style={{ width: '200px', padding: '10px 20px' }}>
                            Submit Exam
                            </Button>
                        </Flex>
                    </form>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Text color="gray">No exams found for this lesson</Text>
                    </div>
                )}
            </Box>
        </div>
    );
};

export default Exam;